/**
 * careerPathApi.ts
 *
 * Dual-mode API wrapper for career path content and journey records.
 *
 * - config.appMode === 'prototype'  → reads/writes StorageService + demo data (no network)
 * - otherwise                       → hits the real REST API via apiClient (with auth)
 */

import config from '../../../config';
import api from './apiClient';
import { StorageService } from '@/hooks/services/storageService';
import { getDemoPathContent } from '@/data/sample/careerPathDemo';
import type { CareerPathContent, UserJourneyRecord } from '@/types/journey';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function loadRecords(): UserJourneyRecord[] {
  const raw = StorageService.read('journeyRecords');
  return Array.isArray(raw) ? raw : [];
}

function saveRecords(records: UserJourneyRecord[]) {
  StorageService.write('journeyRecords', records);
}

function makeId(prefix: string) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Unwrap backend envelope: { success, data: { ... } } */
function unwrap<T>(res: { data: { data: T } }): T {
  return res.data.data;
}

// ─── careerPathApi ────────────────────────────────────────────────────────────

export const careerPathApi = {
  // ── Content ──────────────────────────────────────────────────────────────

  async getContent(pathId: string): Promise<CareerPathContent> {
    if (config.appMode === 'prototype') {
      return getDemoPathContent(pathId);
    }
    try {
      const res = await api.get(`/career-paths/${pathId}`);
      const data = unwrap<{ path: CareerPathContent }>(res);
      return data.path;
    } catch (err: any) {
      // Path not seeded in DB yet — use local demo content so the page isn't blank
      if (err?.response?.status === 404) {
        return getDemoPathContent(pathId);
      }
      throw err;
    }
  },

  // ── Journey Records ───────────────────────────────────────────────────────

  async getJourney(_userId: string, pathId: string): Promise<UserJourneyRecord | null> {
    if (config.appMode === 'prototype') {
      const all = loadRecords();
      return all.find((r) => r.pathId === pathId) ?? null;
    }
    try {
      const res = await api.get(`/journey/me/${pathId}`);
      const data = unwrap<{ journey: UserJourneyRecord }>(res);
      return data.journey;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  },

  async getAllJourneys(_userId: string): Promise<UserJourneyRecord[]> {
    if (config.appMode === 'prototype') {
      return loadRecords();
    }
    const res = await api.get('/journey/me');
    const data = unwrap<{ journeys: UserJourneyRecord[] }>(res);
    return data.journeys ?? [];
  },

  async enroll(
    _userId: string,
    pathId: string,
    pathLevel: UserJourneyRecord['pathLevel'],
    pathName: string,
    totalModules: number,
    firstModuleId: string,
  ): Promise<UserJourneyRecord> {
    if (config.appMode === 'prototype') {
      const records = loadRecords();
      const existing = records.find((r) => r.pathId === pathId);
      if (existing) return existing;

      const now = new Date().toISOString();
      const record: UserJourneyRecord = {
        _id: makeId('journey'),
        userId: '',
        pathId,
        pathLevel,
        pathName,
        enrolledAt: now,
        lastAccessedAt: now,
        completedModuleIds: [],
        currentModuleId: firstModuleId,
        progressPercent: 0,
        totalModules,
        quizScores: {},
        totalXpEarned: 0,
      };
      saveRecords([...records, record]);
      return record;
    }
    const res = await api.post('/journey/enroll', { pathId, pathName, totalModules, selectedBroadPathId: pathId });
    const data = unwrap<{ journey: UserJourneyRecord }>(res);
    return data.journey;
  },

  async completeModule(
    _userId: string,
    pathId: string,
    moduleId: string,
    quizScore?: number,
    xpEarned?: number,
  ): Promise<UserJourneyRecord> {
    if (config.appMode === 'prototype') {
      const records = loadRecords();
      const idx = records.findIndex((r) => r.pathId === pathId);
      if (idx === -1) throw new Error('Journey record not found');

      const rec = { ...records[idx] };
      if (!rec.completedModuleIds.includes(moduleId)) {
        rec.completedModuleIds = [...rec.completedModuleIds, moduleId];
      }
      if (quizScore !== undefined) {
        rec.quizScores = { ...rec.quizScores, [moduleId]: quizScore };
      }
      if (xpEarned) {
        rec.totalXpEarned += xpEarned;
      }
      rec.progressPercent = Math.round(
        (rec.completedModuleIds.length / rec.totalModules) * 100
      );
      rec.lastAccessedAt = new Date().toISOString();

      records[idx] = rec;
      saveRecords(records);

      localStorage.setItem('journey_progress', JSON.stringify({
        lastPathId:      rec.pathId,
        lastPathName:    rec.pathName,
        lastLessonTitle: moduleId,
        lastLessonUrl:   `/journey?pathId=${rec.pathId}&module=${moduleId}`,
        progressPercent: rec.progressPercent,
      }));

      return rec;
    }
    const res = await api.post(`/journey/me/${pathId}/modules/complete`, { moduleId, quizScore, xpEarned });
    const data = unwrap<{ journey: UserJourneyRecord }>(res);
    return data.journey;
  },

  async updateCurrentModule(
    _userId: string,
    pathId: string,
    moduleId: string,
  ): Promise<void> {
    if (config.appMode === 'prototype') {
      const records = loadRecords();
      const idx = records.findIndex((r) => r.pathId === pathId);
      if (idx === -1) return;
      records[idx] = {
        ...records[idx],
        currentModuleId: moduleId,
        lastAccessedAt: new Date().toISOString(),
      };
      saveRecords(records);
      return;
    }
    await api.patch(`/journey/me/${pathId}/current-module`, { moduleId });
  },

  deleteJourneysForPath(_userId: string, pathId: string): void {
    if (config.appMode === 'prototype') {
      const records = loadRecords();
      const remaining = records.filter(
        (r) => !(r.pathId === pathId || r.pathId.startsWith(pathId))
      );
      saveRecords(remaining);

      try {
        const raw = localStorage.getItem('journey_progress');
        if (raw) {
          const jp = JSON.parse(raw);
          if (jp.lastPathId === pathId || jp.lastPathId?.startsWith(pathId)) {
            localStorage.removeItem('journey_progress');
          }
        }
      } catch { /* ignore */ }
      return;
    }
    api.delete(`/journey/me/${pathId}`).catch(() => {});
  },
};
